import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, forkJoin, tap } from 'rxjs';
import config from '../../../../config.js';

export interface UserPermissionsSyncResponse {
  message?: string;
  success?: boolean;
}

export interface HasPermissionResponse {
  has_permission: boolean;
}

export interface BatchPermissionCheck {
  role_name: string[];
  module_name: string[];
  permission_key: string;
}

export interface BatchPermissionResponse {
  permissions: {
    [key: string]: boolean;
  };
}

@Injectable({ providedIn: 'root' })
export class UserPermissionsService {
  private readonly base = config.API_GATEWAY_ENDPOINT?.replace(/\/$/, '') || '';
  private readonly endpoint = `${this.base}/user_permissions/sync`;

  // Global permission signals
  hasAnyBcrRole = signal<boolean>(false); // For global access control
  permissionsLoaded = signal<boolean>(false); // True once checkPermissionsBatch completes
  hasStatusDropdownPermission = signal<boolean>(false);
  hasUploadDocumentPermission = signal<boolean>(false); // For Replace File button
  hasEditEntityIdentificationPermission = signal<boolean>(false); // For Entity Identification edit
  hasEditContactInformationPermission = signal<boolean>(false); // For Contact Information edit/add
  hasUploadBncsDocumentPermission = signal<boolean>(false); // For B&CS Assessment Upload button
  hasDocumentRepositoryUploadPermission = signal<boolean>(false); // For Document Repository Replace File button

  // DDQ-Specific Fields permissions
  hasPstMemberRole = signal<boolean>(false); // For editing DDQ Nominee and Contact Email
  hasDdqNomineeRole = signal<boolean>(false); // For viewing DDQ File upload section
  hasPrivacyOfficeRole = signal<boolean>(false); // For DDQ approval/rejection actions

  // List of all valid BCR roles
  private readonly BCR_ROLES = [
    'BCR - BCS Team Member',
    'BCR - PST member',
    'BCR - Privacy Office team member',
    'BCR - IGA Coordinator',
    'BCR - DDQ Nominee',
    'XAZ-BCR Approver',
    'BCR - Viewer',
  ];

  constructor(private http: HttpClient) {}

  /**
   * Sync user permissions after login.
   * POST to /user_permissions/sync
   */

  syncUserPermissions(): Observable<UserPermissionsSyncResponse> {
    return this.http.post<UserPermissionsSyncResponse>(this.endpoint, {});
  }

  /**
   * Checks multiple permissions in a single batch operation using forkJoin.
   * Deduplicates identical role+module combinations to minimize API calls.
   * All unique API calls are made simultaneously and signals are updated.
   *
   * @param pridUser - User PRID (username before @)
   * @param checks - Array of permission checks with their signal mappings
   * @returns Observable that completes when all checks are done
   */
  checkPermissionsBatch(
    pridUser: string,
    checks: Array<{
      roles: string[];
      modules: string[];
      signal: ReturnType<typeof signal<boolean>>;
      key: string;
    }>,
  ): Observable<void> {
    if (!pridUser || checks.length === 0) {
      checks.forEach((check) => check.signal.set(false));
      return of(void 0);
    }

    const endpoint = `${this.base}/user_permissions/has_permission`;

    // Group checks by unique role+module combination to avoid duplicate API calls
    const uniqueChecksMap = new Map<string, Array<(typeof checks)[0]>>();

    checks.forEach((check) => {
      // Create a unique key for this role+module combination
      const key = JSON.stringify({ roles: check.roles.sort(), modules: check.modules.sort() });

      if (!uniqueChecksMap.has(key)) {
        uniqueChecksMap.set(key, []);
      }
      uniqueChecksMap.get(key)!.push(check);
    });

    // Create only one request per unique role+module combination
    const requests = Array.from(uniqueChecksMap.entries()).map(([_key, groupedChecks]) => {
      const firstCheck = groupedChecks[0];
      const body = {
        prid_user: pridUser,
        role_name: firstCheck.roles,
        module_name: firstCheck.modules,
      };

      return this.http.post<HasPermissionResponse>(endpoint, body).pipe(
        tap((res) => {
          // Update all signals that share this role+module combination
          const hasPermission = !!res?.has_permission;
          groupedChecks.forEach((check) => check.signal.set(hasPermission));
        }),
        catchError(() => {
          // Set all grouped signals to false on error
          groupedChecks.forEach((check) => check.signal.set(false));
          return of({ has_permission: false });
        }),
      );
    });

    return forkJoin(requests.length > 0 ? requests : [of(void 0)]).pipe(
      tap(() => {
        this.permissionsLoaded.set(true);
      }),
      catchError(() => {
        checks.forEach((check) => check.signal.set(false));
        this.permissionsLoaded.set(true);
        return of(void 0);
      }),
      // Map to void to match return type
      tap(() => void 0),
    ) as Observable<void>;
  }

  /**
   * Checks if user has permission for a specific role and module.
   * Updates the corresponding permission signal with the result.
   * This is called by AuthGuard after successful authentication.
   *
   * @param pridUser - User PRID (username before @)
   * @param roleName - Role name to check (e.g., 'BCR - PST member')
   * @param moduleName - Module name to check (e.g., 'Legal Entity List')
   * @param permissionSignal - Signal to update with the result
   */
  checkPermission(
    pridUser: string,
    roleName: string[],
    moduleName: string[],
    permissionSignal: ReturnType<typeof signal<boolean>>,
  ): void {
    if (!pridUser) {
      permissionSignal.set(false);
      return;
    }

    const endpoint = `${this.base}/user_permissions/has_permission`;
    const body = {
      prid_user: pridUser,
      role_name: roleName,
      module_name: moduleName,
    };

    this.http
      .post<HasPermissionResponse>(endpoint, body)
      .pipe(catchError(() => of({ has_permission: false })))
      .subscribe((res) => {
        permissionSignal.set(!!res?.has_permission);
      });
  }

  /**
   * Checks if user has ANY of the BCR roles.
   * This is used for global access control.
   * Returns an Observable that emits the response with has_permission property.
   *
   * @param pridUser - User PRID (username before @)
   * @returns Observable<HasPermissionResponse>
   */
  checkGlobalAccess(pridUser: string): Observable<HasPermissionResponse> {
    if (!pridUser) {
      this.hasAnyBcrRole.set(false);
      return of({ has_permission: false });
    }

    const endpoint = `${this.base}/user_permissions/has_permission`;
    const body = {
      prid_user: pridUser,
      role_name: this.BCR_ROLES,
      module_name: [], // Empty array means check roles regardless of module
    };

    return this.http.post<HasPermissionResponse>(endpoint, body).pipe(
      catchError(() => {
        this.hasAnyBcrRole.set(false);
        return of({ has_permission: false });
      }),
    );
  }
}
